import { useState } from "react";
import { Run } from "./Run";
import { createEntityStore } from "../../common/entityStore";
import { Button, Grid } from "@mui/material";
import { propBoolField, propCustomEnumField, propDateField, propFileField } from "../../common/entityStoreForm";
import { propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { today } from "../../common/utils";
import { useTransactionCodes, useWarehouses } from "../../common/enum";
import { createListStore } from "../../common/listStore";
import { combineLatest, map } from "rxjs";
import { DefaultValuesDto, useDefaultValues } from "../../common/defaultValues";
import { downloadFromApi } from "../../common/download";

interface ReturnType {
    id: number;
    warehouseId: number;
    transactionCodeId: number;
    name: string;
}

export function ReturnRunPage() {
    const def = useDefaultValues();
    const warehouses = useWarehouses();
    const transactionCodes = useTransactionCodes();
    if (!def.isFetched || !warehouses.fetched || !transactionCodes.fetched) {
        return null;
    } else if (
        def.clientData.customerReturnTransactionCodeId == null ||
        def.clientData.customerReturnWarehouseId == null ||
        def.clientData.postalReturnTransactionCodeId == null ||
        def.clientData.postalReturnWarehouseId == null) {
        return <>Return warehouses or transactions are not configured, configure them on Default values page.</>;
    } else {
        return <ReturnRunPageInternal def={def.clientData} />;
    }
}

function ReturnRunPageInternal(props: { def: DefaultValuesDto }) {
    const [{ returnTypes, defaultReturnType, useReturnTypes }] = useState(() => {
        const returnTypes: { id: number; transactionCodeId: number; warehouseId: number; }[] = [
            { id: 1, transactionCodeId: props.def.postalReturnTransactionCodeId!, warehouseId: props.def.postalReturnWarehouseId! },
            { id: 2, transactionCodeId: props.def.customerReturnTransactionCodeId!, warehouseId: props.def.customerReturnWarehouseId! },
        ]
        const defaultReturnType = returnTypes[0];
        const useReturnTypes = createListStore<ReturnType>({
            data: combineLatest([useTransactionCodes.getState().items$, useWarehouses.getState().items$]).pipe(map(([transactionCodes, warehouses]) => {
                return {
                    items: returnTypes.map(p => ({
                        id: p.id,
                        transactionCodeId: p.transactionCodeId,
                        warehouseId: p.warehouseId,
                        name: `${transactionCodes.items.find(t => t.id === p.transactionCodeId)?.name} (${warehouses.items.find(w => w.id === p.warehouseId)?.name})`
                    })),
                    isFetching: false,
                }
            }))
        });
        return { returnTypes, defaultReturnType, useReturnTypes };
    });
    const [useParamsStore] = useState(() => createEntityStore<RunParams>({
        data: {
            retDate: today(),
            warehouseId: defaultReturnType.warehouseId,
            transactionCodeId: defaultReturnType.transactionCodeId,
            type: defaultReturnType.id,
            makePass: false,
            blockCust: false,
        },
        id: null,
        propValidators: [
            propRequired("file"),
            propValidValue("retDate", "retDateFormatted"),
            propRequired("retDate"),
            propValueValidator("isFileImported", p => p && "File is already imported."),
        ]
    }));
    const { makePass } = useParamsStore(p => ({ makePass: p.clientData.makePass }));
    const [isRunning, setIsRunning] = useState(false);
    const [runId, setRunId] = useState(0);

    function renderParams() {
        return <>
            <Grid item xs={12}>{propFileField(useParamsStore)("file", "File", { disabled: isRunning, onChange: () => useParamsStore.getState().setClientDataProp("isFileImported", undefined) })}</Grid>
            <Grid item xs={12}>{propDateField(useParamsStore)("retDate", "retDateFormatted", "Date")}</Grid>
            <Grid item xs={12}>{propCustomEnumField(useParamsStore)("type", "Return type", useReturnTypes, {
                getId: p => p.id,
                getName: p => p.name,
                onChange: p => {
                    const returnType = returnTypes.find(r => r.id === p);
                    if (returnType) {
                        useParamsStore.getState().setClientDataProp("warehouseId", returnType.warehouseId);
                        useParamsStore.getState().setClientDataProp("transactionCodeId", returnType.transactionCodeId);
                    }
                }
            })}</Grid>
            <Grid item xs={12}>{propBoolField(useParamsStore)("makePass", "Deactivate after return?", {
                onChange: () => useParamsStore.getState().setClientDataProp("blockCust", false),
            })}</Grid>
            <Grid item xs={12}>{propBoolField(useParamsStore)("blockCust", "Block customer", { disabled: !makePass })}</Grid>
            <Button onClick={() => downloadFromApi(`runs/runs/${runId}/export`)} disabled={!runId}>Export logs</Button>
        </>;
    }
    return <>
        <Run name="Batch returns" hub="returnRun" onRunFinished={p => p && useParamsStore.getState().setClientDataProp("isFileImported", true)}
            useParamsStore={useParamsStore} renderParams={renderParams} setIsRunning={setIsRunning} processResult={(result) => setRunId(result.runId)} />
    </>;
}

interface RunParams {
    file?: File;
    isFileImported?: boolean;
    warehouseId: number;
    transactionCodeId: number;
    retDate: string;
    retDateFormatted?: string;
    makePass: boolean;
    blockCust: boolean;
    type?: number;
}
